import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ALL_NATIONS, nationTranslations, onboardingTranslations } from "@/data/nationTranslations";

interface NationOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function NationOnboardingModal({ isOpen, onClose, onSuccess }: NationOnboardingModalProps) {
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [selectedNation, setSelectedNation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const t = onboardingTranslations[language];
  const nations = nationTranslations[language];

  const handleSave = async () => {
    if (!user || !selectedNation) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          favorite_nation: selectedNation,
          updated_at: new Date().toISOString()
        })
        .eq("user_id", user.id);

      if (error) throw error;

      toast.success(t.successMessage);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving favorite nation:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // Mark as skipped in localStorage so we don't show again this session
    localStorage.setItem("nation_onboarding_skipped", "true");
    onClose();
  };

  const Content = () => (
    <div className="flex flex-col items-center text-center py-4">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
          <Globe className="w-10 h-10 text-primary" />
        </div>
      </div>

      <h3 className="font-display text-xl font-bold text-foreground mb-2">
        {t.welcomeTitle}
      </h3>
      <p className="text-muted-foreground mb-6 max-w-sm">
        {t.welcomeSubtitle}
      </p>

      {/* Nation Selection with Flags */}
      <div className="w-full mb-6">
        <Select
          value={selectedNation || ""}
          onValueChange={(value) => setSelectedNation(value || null)}
        >
          <SelectTrigger className="w-full bg-background text-left">
            <SelectValue placeholder={t.selectPlaceholder}>
              {selectedNation && (
                <span className="flex items-center gap-2">
                  <span className="text-lg">{nations[selectedNation as keyof typeof nations]?.flag}</span>
                  <span>{nations[selectedNation as keyof typeof nations]?.name}</span>
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {ALL_NATIONS.map((code) => (
              <SelectItem key={code} value={code}>
                <span className="flex items-center gap-2">
                  <span className="text-lg">{nations[code].flag}</span>
                  <span>{nations[code].name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-full space-y-3">
        <Button 
          onClick={handleSave} 
          disabled={!selectedNation || isLoading}
          className="w-full gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            t.saveButton
          )}
        </Button>
        <Button 
          onClick={handleSkip} 
          variant="ghost" 
          className="w-full text-muted-foreground"
        >
          {t.skipButton}
        </Button>
      </div>

      {/* Top 8 Nation Flags Preview */}
      <div className="mt-6 flex items-center justify-center gap-1.5 flex-wrap">
        {ALL_NATIONS.slice(0, 8).map((code) => (
          <span 
            key={code} 
            className={`text-xl transition-all ${selectedNation === code ? 'opacity-100 scale-125' : 'opacity-40'}`}
          >
            {nations[code].flag}
          </span>
        ))}
        <span className="text-muted-foreground text-sm ml-1">+16 more</span>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="text-center">
            <DrawerTitle className="sr-only">Choose Your Nation</DrawerTitle>
            <DrawerDescription className="sr-only">
              Select your favorite national team
            </DrawerDescription>
          </DrawerHeader>
          <Content />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="sr-only">Choose Your Nation</DialogTitle>
          <DialogDescription className="sr-only">
            Select your favorite national team
          </DialogDescription>
        </DialogHeader>
        <Content />
      </DialogContent>
    </Dialog>
  );
}
